import React, { useState } from 'react';
import { ECardTitle, ECardSubTitle, ECardWrapper, StyledCell } from '@components/GalleryComponents/GalleryGrid/GalleryGrid.styles';
import { GalleryECard } from '@components/GalleryComponents/GalleryECard/GalleryECard';
import { DEFAULT_VENDOR } from '@components/GalleryComponents/GalleryComponents.constants';
import { capitalize, snakeToCamelTrimmed } from '@utils/strings.util';
import { useTranslation } from 'react-i18next';
import { Text } from '@components/typography';
import { Flex } from '@components/Flex';

interface GalleryECardCellPriceProps
  extends Readonly<{
    minorUnits?: number;
  }> {}

const DISCOUNT_PERCENTAGE = 20;

const GalleryECardCellPrice: React.FC<GalleryECardCellPriceProps> = ({ minorUnits }) => {
  const { t } = useTranslation('galleryPage');

  if (minorUnits === undefined) {
    return null;
  }

  const formattedPricePreDiscount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(minorUnits / 100);

  const formattedPrice = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format((minorUnits * (1 - DISCOUNT_PERCENTAGE / 100)) / 100);

  return (
    <>
      <Flex marginTop={1} alignItems="center" justifyContent="center" style={{ gap: '0.25rem' }}>
        <Text tagName="span" variant="featureCopy17" color="neutra4" textAlign="center">
          {t('eCardDetailData.asLowAs')}
        </Text>
        <Text tagName="span" variant="featureCopy17" color="neutra4" textAlign="center" style={{ textDecoration: 'line-through' }}>
          {formattedPricePreDiscount}
        </Text>
        <Text tagName="span" variant="sectionEyeBrow24" color="purple4" textAlign="center">
          {formattedPrice}
        </Text>
      </Flex>
      <span itemProp="offers" itemScope itemType="https://schema.org/Offer">
        <link itemProp="url" href="https://withjoy.com/wedding-invitations" />
        <meta itemProp="priceCurrency" content="USD" />
        <meta itemProp="price" content={(minorUnits / 100).toFixed(2)} />
        <meta itemProp="discount" content={DISCOUNT_PERCENTAGE.toString()} />
        <link itemProp="itemCondition" href="https://schema.org/NewCondition" />
        <link itemProp="availability" href="https://schema.org/InStock" />
      </span>
    </>
  );
};

interface GalleryECardCellProps
  extends Readonly<{
    index: number;
    card: GalleryECard;
    marginTop?: number;
    isSlider?: boolean;
    showInfo?: boolean;
    onCardClick: (card: GalleryECard) => void;
  }> {}

export const GalleryECardCell: React.FC<GalleryECardCellProps> = ({
  index,
  card,
  onCardClick,
  marginTop = 11,
  isSlider = false,
  showInfo = true,
}) => {
  const { t } = useTranslation('galleryPage');
  const [isHovered, setHovered] = useState(false);

  const productName = capitalize(card.variantName ? card.variantName : card.theme?.name);

  return (
    <div itemScope itemType="https://schema.org/Product">
      <StyledCell
        id={`grid${snakeToCamelTrimmed(card.variantName ? card.variantName : card.theme?.name)}${capitalize(card.variationType)}`}
        marginTop={marginTop}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => onCardClick(card)}
        skipRedirectWithTelemetry
      >
        <meta itemProp="sku" content={card.variantThemeId} />
        <meta
          itemProp="description"
          content={t('eCardDetailData.productSchemaDescription', { productName, productType: card.productType })}
        />
        <span itemProp="brand" itemScope itemType="https://schema.org/Brand">
          <meta itemProp="name" content={card.vendor} />
        </span>
        <ECardWrapper $isHovered={isHovered}>
          <GalleryECard index={index} isSlider={isSlider} data={card} isHovered={isHovered} />
        </ECardWrapper>
        {showInfo && (
          <>
            <ECardTitle className="ecard_title" tagName={'p'} variant={'featureCopy17'} color={'neutra6'} textAlign={'center'}>
              <span itemProp="name">{productName}</span>
            </ECardTitle>
            <ECardSubTitle tagName={'p'} variant={'featureCopy17'} color={'neutra4'} textAlign={'center'}>
              {card.vendor === DEFAULT_VENDOR ? '' : card.vendor}
            </ECardSubTitle>
            {card.productType === 'Invitation' && <GalleryECardCellPrice minorUnits={card.stationeryPrintCardLowestPriceInMinorUnits} />}
          </>
        )}
      </StyledCell>
    </div>
  );
};
